import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; 
import { db, storage } from '../utils/firebaseConfig';
import { collection, addDoc, getDocs, query, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import GeneralNavbar from './GeneralNavbar';
import './ProductForm.css';
import { getNextSharedId } from '../utils/counterService';
import { cacheService, CACHE_KEYS } from '../utils/cacheService';

const ProductForm = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState(''); // Nueva: selección de marca
  const [imageFile, setImageFile] = useState(null);
  const [discount, setDiscount] = useState('');
  const [discountInPesos, setDiscountInPesos] = useState('');
  const [categories, setCategories] = useState([]); // Lista de categorías
  const [brands, setBrands] = useState([]); // Lista de marcas según categoría
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate(); 

  const handleBackClick = () => {
    navigate('/admin'); 
  };

  // 🔹 Cargar categorías al montar el componente
  useEffect(() => {
    loadCategories();
  }, []);

  // 🔹 Cargar marcas cuando cambia la categoría
  useEffect(() => {
    if (category) {
      loadBrandsByCategory(category);
      setBrand(''); // Resetear marca cuando cambia categoría
    } else {
      setBrands([]);
    }
  }, [category]);

  // 🔹 Cargar lista de categorías (con caché)
  const loadCategories = async () => {
    try {
      setLoading(true);
      
      // 🚀 Verificar caché primero
      const cached = cacheService.get(CACHE_KEYS.CATEGORIES);
      if (cached) {
        setCategories(cached);
        setLoading(false);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const q = query(collection(db, 'categories'), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const categoryList = snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(CACHE_KEYS.CATEGORIES, categoryList);
      
      setCategories(categoryList);
      setLoading(false);
    } catch (error) {
      console.error("Error cargando categorías:", error);
      setLoading(false);
    }
  };

  // 🔹 Cargar marcas según categoría seleccionada (con caché)
  const loadBrandsByCategory = async (categoryId) => {
    try {
      // 🚀 Verificar caché primero
      const cacheKey = CACHE_KEYS.BRANDS(categoryId);
      const cached = cacheService.get(cacheKey);
      if (cached) {
        setBrands(cached);
        return;
      }

      // Si no hay caché, cargar de Firebase
      const brandsSnapshot = await getDocs(
        collection(db, 'categories', categoryId, 'brands')
      );
      
      const brandList = brandsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name
      }));
      
      // 💾 Guardar en caché
      cacheService.set(cacheKey, brandList);
      
      setBrands(brandList);
    } catch (error) {
      console.error("Error cargando marcas:", error);
      setBrands([]);
    }
  };

  // 🔹 Manejar cambio de marca - actualizar name automáticamente
  const handleBrandChange = (brandId) => {
    setBrand(brandId);
    const selectedBrand = brands.find(b => b.id === brandId);
    if (selectedBrand) {
      setName(selectedBrand.name); // Automáticamente asignar nombre de marca
    }
  };


  const handleImageUpload = async (file) => {
    if (!file) return null;

    const storageRef = ref(storage, `product-images/${file.name}`);
    await uploadBytes(storageRef, file);
    const imageUrl = await getDownloadURL(storageRef);
    return imageUrl;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    let imageUrl = '';
    if (imageFile) {
      imageUrl = await handleImageUpload(imageFile);
    }
  
    const productId = await getNextSharedId(); // Usar el contador compartido
  
    // No calcular el precio con descuento aquí, solo guardar el precio original y el descuento
    const productData = {
    id: productId,
    name,
    description,
    price: parseFloat(price),
    stock: parseInt(stock),
    category,
    brand, // Incluir marca
    discount: discount ? parseFloat(discount) : 0,
    discountInPesos: discountInPesos ? parseFloat(discountInPesos) : 0,
    image: imageUrl,
    enabled: true,
    createdAt: new Date(), // ✅ Agregar fecha de creación
  };

  
    try {
      const productCollectionRef = collection(db, 'products');
      await addDoc(productCollectionRef, productData);
      alert('Producto agregado correctamente');
  
      // Limpiar los campos después de agregar el producto
      setName('');
      setDescription('');
      setPrice('');
      setStock('');
      setCategory('');
      setBrand('');
      setDiscount('');
      setDiscountInPesos('');
      setImageFile(null);
    } catch (error) {
      console.error('Error al registrar el producto: ', error);
    }
  };
  

  return (
    <>
      <GeneralNavbar title="Agregar Producto" onBackClick={handleBackClick} />
      <div className="product-form-wrapper">
        <div className="product-form-container">
          <form onSubmit={handleSubmit}>
            {/* 🔹 Menú desplegable para Categoría */}
            <div className="product-form-group">
              <label className="product-form-label">Categoría:</label>
              <select 
                className="product-form-input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                disabled={loading}
              >
                <option value="">Selecciona una categoría</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 🔹 Menú desplegable para Marca (dinámico) - REEMPLAZA al input de nombre */}
            {category && (
              <div className="product-form-group">
                <label className="product-form-label">Marca del Producto:</label>
                <select 
                  className="product-form-input"
                  value={brand}
                  onChange={(e) => handleBrandChange(e.target.value)}
                  required
                >
                  <option value="">Selecciona una marca</option>
                  {brands.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Campo de descripción - ahora viene después de marca */}
            <div className="product-form-group">
              <label className="product-form-label">Descripción:</label>
              <input 
                type="text" 
                className="product-form-input"
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                required 
              />
            </div>
            <div className="product-form-group">
              <label className="product-form-label">Precio:</label>
              <input 
                type="number" 
                className="product-form-input"
                value={price} 
                onChange={(e) => setPrice(e.target.value)} 
                required 
              />
            </div>
            <div className="product-form-group">
              <label className="product-form-label">Stock:</label>
              <input 
                type="number" 
                className="product-form-input"
                value={stock} 
                onChange={(e) => setStock(e.target.value)} 
                required 
              />
            </div>
           {/* Campo para porcentaje de oferta */}
            <div className="product-form-group">
              <label className="product-form-label">Descuento en porcentaje (%):</label>
              <input 
                type="number" 
                className="product-form-input"
                value={discount} 
                onChange={(e) => {
                  setDiscount(e.target.value);
                  if (e.target.value) setDiscountInPesos('');
                }}
                min="0" 
                max="100" 
                disabled={!!discountInPesos}
              />
            </div>

            {/* Campo para oferta en pesos */}
            <div className="product-form-group">
              <label className="product-form-label">Descuento en pesos ($):</label>
              <input 
                type="number" 
                className="product-form-input"
                value={discountInPesos} 
                onChange={(e) => {
                  setDiscountInPesos(e.target.value);
                  if (e.target.value) setDiscount('');
                }}
                min="0"
                disabled={!!discount}
              />
            </div>

            <div className="product-form-group">
              <label className="product-form-label">Imagen:</label>
              <input 
                type="file" 
                className="product-form-input"
                onChange={(e) => setImageFile(e.target.files[0])} 
              />
            </div>
            <button type="submit" className="product-form-button">
              Agregar
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ProductForm;



